/**
 * Workaround: hide the map pin icon rendered by OpenLayersGetFeatureInfo.
 *
 * The upstream component couples the click listener lifecycle to
 * displayMapPin — setting it to false removes both the icon AND the
 * listener.  We keep displayMapPin=true so clicks still fire, and this
 * component programmatically hides the pin's VectorLayer (zIndex 9000)
 * via the OpenLayers API.
 */

import type BaseLayer from 'ol/layer/Base';
import { useContext, useEffect } from 'react';
import { MapContext } from '@opengeoweb/webmap-react';

const PIN_Z_INDEX = 9000;

function isPinLayer(layer: BaseLayer): boolean {
  return (
    layer.getZIndex() === PIN_Z_INDEX &&
    layer.get('layerType') === 'FeatureLayer'
  );
}

const HideMapPin: React.FC = () => {
  const { map } = useContext(MapContext);

  useEffect(() => {
    if (!map) {
      return undefined;
    }

    // Hide any pin layer that already exists
    map.getLayers().forEach((layer) => {
      if (isPinLayer(layer)) {
        layer.setVisible(false);
      }
    });

    // Hide pin layers added later (the FeatureLayer mounts asynchronously)
    const layers = map.getLayers();
    const onAdd = (e: { element: BaseLayer }): void => {
      if (isPinLayer(e.element)) {
        e.element.setVisible(false);
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    layers.on('add' as any, onAdd as any);

    return (): void => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      layers.un('add' as any, onAdd as any);
    };
  }, [map]);

  return null;
};

export default HideMapPin;
